export type AnswerArithmeticClaimKind =
  | 'sum'
  | 'percent'
  | 'same_binding'
  | 'delta';

export type AnswerArithmeticFindingSeverity = 'warning' | 'error';

export type AnswerArithmeticFinding = {
  component: string;
  claim: AnswerArithmeticClaimKind;
  expected: number | string;
  actual: number | string;
  severity: AnswerArithmeticFindingSeverity;
};

export type AnswerArithmeticAuditOptions = {
  absoluteTolerance?: number;
  relativeTolerance?: number;
  percentTolerance?: number;
};

type UnknownRecord = Record<string, unknown>;

type ComponentNode = {
  id: string;
  record: UnknownRecord;
};

type BindingOccurrence = {
  bindingId: string;
  component: string;
  value: number;
  scale: string | null;
  source: 'binding' | 'rendered';
  roundingTolerance: number;
};

type SumValuesResult = {
  total: number | null;
  nonNumericPart: string | null;
};

const DEFAULT_ABSOLUTE_TOLERANCE = 0.01;
const DEFAULT_RELATIVE_TOLERANCE = 1e-9;
const DEFAULT_PERCENT_TOLERANCE = 0.01;

export function auditAnswerArithmetic(
  envelope: unknown,
  options: AnswerArithmeticAuditOptions = {},
): AnswerArithmeticFinding[] {
  const findings: AnswerArithmeticFinding[] = [];
  const components = collectComponents(envelope);

  findings.push(...auditNumericBindingSums(envelope, options));
  findings.push(...auditComponentClaims(components, options));
  findings.push(...auditSameBindingConsistency(envelope, components, options));

  return findings;
}

function auditNumericBindingSums(
  envelope: unknown,
  options: AnswerArithmeticAuditOptions,
): AnswerArithmeticFinding[] {
  const findings: AnswerArithmeticFinding[] = [];

  for (const binding of collectNumericBindings(envelope)) {
    if (isSumExempt(binding)) continue;

    const reconciliation = toRecord(binding.reconciliation);
    if (!reconciliation) continue;

    const parts = toArray(reconciliation.components);
    const method = stringValue(reconciliation.method);
    if (method !== 'sum_of_parts') continue;
    if (parts.length === 0) continue;

    const total = firstNumber(reconciliation.total, binding.value, binding.resolvedValue);
    if (total === null) continue;

    const partsTotal = sumValues(parts);
    if (partsTotal.nonNumericPart) {
      findings.push({
        component: `binding:${bindingIdentifier(binding)}`,
        claim: 'sum',
        expected: total,
        actual: `non-numeric part: ${partsTotal.nonNumericPart}`,
        severity: 'error',
      });
      continue;
    }
    if (partsTotal.total === null) continue;

    const residual = toRecord(reconciliation.residual);
    const residualValue = residual ? numberValue(residual.value) : null;
    const actual = partsTotal.total + (residualValue ?? 0);

    if (!withinTolerance(total, actual, options)) {
      findings.push({
        component: `binding:${bindingIdentifier(binding)}`,
        claim: 'sum',
        expected: total,
        actual,
        severity: 'error',
      });
    }
  }

  return findings;
}

function auditComponentClaims(
  components: ComponentNode[],
  options: AnswerArithmeticAuditOptions,
): AnswerArithmeticFinding[] {
  const findings: AnswerArithmeticFinding[] = [];

  for (const component of components) {
    const claims = collectArithmeticClaims(component.record);
    for (const claim of claims) {
      const kind = stringValue(claim.kind ?? claim.type);
      if (kind === 'sum') {
        if (!isSumExempt(component.record)) {
          const finding = auditSumClaim(component.id, claim, options);
          if (finding) findings.push(finding);
        }
      } else if (kind === 'percent') {
        const finding = auditPercentClaim(component.id, claim, options);
        if (finding) findings.push(finding);
      } else if (kind === 'delta') {
        const finding = auditDeltaClaim(component.id, claim, options);
        if (finding) findings.push(finding);
      }
    }
  }

  return findings;
}

function auditSumClaim(
  component: string,
  claim: UnknownRecord,
  options: AnswerArithmeticAuditOptions,
): AnswerArithmeticFinding | null {
  const total = firstNumber(claim.total, claim.expected, claim.statedTotal);
  if (total === null) return null;

  const parts = toArray(claim.parts ?? claim.components);
  const partsTotal = sumValues(parts);
  if (partsTotal.nonNumericPart) {
    return {
      component,
      claim: 'sum',
      expected: total,
      actual: `non-numeric part: ${partsTotal.nonNumericPart}`,
      severity: severityValue(claim.severity),
    };
  }
  if (partsTotal.total === null) return null;

  const residual = firstNumber(claim.residual, toRecord(claim.residual)?.value);
  const actual = partsTotal.total + (residual ?? 0);

  if (withinTolerance(total, actual, options)) return null;
  return {
    component,
    claim: 'sum',
    expected: total,
    actual,
    severity: severityValue(claim.severity),
  };
}

function auditPercentClaim(
  component: string,
  claim: UnknownRecord,
  options: AnswerArithmeticAuditOptions,
): AnswerArithmeticFinding | null {
  const numerator = firstNumber(claim.numerator, claim.value, claim.part);
  const denominator = firstNumber(claim.denominator, claim.base, claim.total);
  const actual = firstNumber(claim.percent, claim.actual, claim.displayedPercent);
  if (numerator === null || denominator === null || actual === null || denominator === 0) {
    return null;
  }

  const expected = (numerator / denominator) * 100;
  const tolerance = numberValue(claim.tolerance) ?? options.percentTolerance ?? DEFAULT_PERCENT_TOLERANCE;
  if (Math.abs(expected - actual) <= tolerance) return null;

  return {
    component,
    claim: 'percent',
    expected,
    actual,
    severity: severityValue(claim.severity),
  };
}

function auditDeltaClaim(
  component: string,
  claim: UnknownRecord,
  options: AnswerArithmeticAuditOptions,
): AnswerArithmeticFinding | null {
  const from = firstNumber(claim.from, claim.baseline, claim.prior, claim.previous);
  const to = firstNumber(claim.to, claim.current, claim.comparison, claim.after);
  const actualDelta = firstNumber(claim.delta, claim.change, claim.actual);
  if (from === null || to === null || actualDelta === null) return null;

  const expectedDelta = to - from;
  const direction = stringValue(claim.direction);
  const expectedDirection = expectedDelta >= 0 ? 'up' : 'down';
  const directionMismatch =
    direction === 'up' || direction === 'down'
      ? direction !== expectedDirection
      : false;

  if (!directionMismatch && withinTolerance(expectedDelta, actualDelta, options)) {
    return null;
  }

  return {
    component,
    claim: 'delta',
    expected: directionMismatch ? `${expectedDirection} ${Math.abs(expectedDelta)}` : expectedDelta,
    actual: directionMismatch ? `${direction} ${Math.abs(actualDelta)}` : actualDelta,
    severity: severityValue(claim.severity),
  };
}

function auditSameBindingConsistency(
  envelope: unknown,
  components: ComponentNode[],
  options: AnswerArithmeticAuditOptions,
): AnswerArithmeticFinding[] {
  const findings: AnswerArithmeticFinding[] = [];
  const groups = new Map<string, BindingOccurrence[]>();

  for (const occurrence of [
    ...collectNumericBindingOccurrences(envelope),
    ...collectRenderedBindingOccurrences(components),
  ]) {
    const group = groups.get(occurrence.bindingId) ?? [];
    group.push(occurrence);
    groups.set(occurrence.bindingId, group);
  }

  for (const occurrences of groups.values()) {
    const canonicalBinding = occurrences.find((occurrence) => occurrence.source === 'binding');
    if (canonicalBinding) {
      for (const occurrence of occurrences.filter((item) => item.source === 'rendered')) {
        const absoluteTolerance = Math.max(
          options.absoluteTolerance ?? DEFAULT_ABSOLUTE_TOLERANCE,
          canonicalBinding.roundingTolerance,
          occurrence.roundingTolerance,
        );
        if (!withinTolerance(canonicalBinding.value, occurrence.value, {
          ...options,
          absoluteTolerance,
        })) {
          findings.push({
            component: occurrence.component,
            claim: 'same_binding',
            expected: canonicalBinding.value,
            actual: occurrence.value,
            severity: 'error',
          });
        }
      }
    }

    for (const source of ['binding', 'rendered'] as const) {
      const sourceOccurrences = occurrences.filter((occurrence) => occurrence.source === source);
      if (sourceOccurrences.length < 2) continue;
      const baseline = sourceOccurrences[0];
      for (const occurrence of sourceOccurrences.slice(1)) {
        const expectedScale = baseline.scale ?? 'unspecified';
        const actualScale = occurrence.scale ?? 'unspecified';
        if (expectedScale !== actualScale) {
          findings.push({
            component: occurrence.component,
            claim: 'same_binding',
            expected: `${baseline.bindingId}: value=${baseline.value}; scale=${expectedScale}`,
            actual: `${occurrence.bindingId}: value=${occurrence.value}; scale=${actualScale}`,
            severity: 'error',
          });
          continue;
        }

        const absoluteTolerance = Math.max(
          options.absoluteTolerance ?? DEFAULT_ABSOLUTE_TOLERANCE,
          baseline.roundingTolerance,
          occurrence.roundingTolerance,
        );
        if (!withinTolerance(baseline.value, occurrence.value, {
          ...options,
          absoluteTolerance,
        })) {
          findings.push({
            component: occurrence.component,
            claim: 'same_binding',
            expected: baseline.value,
            actual: occurrence.value,
            severity: 'error',
          });
        }
      }
    }
  }

  return findings;
}

function collectArithmeticClaims(component: UnknownRecord): UnknownRecord[] {
  const props = toRecord(component.props);
  const claims: UnknownRecord[] = [];

  appendRecordArray(claims, component.arithmeticClaims);
  appendRecordArray(claims, props?.arithmeticClaims);
  appendKindedClaims(claims, 'sum', component.sumClaims, props?.sumClaims);
  appendKindedClaims(claims, 'percent', component.percentClaims, props?.percentClaims);
  appendKindedClaims(claims, 'delta', component.deltaClaims, props?.deltaClaims);

  for (const reconciliation of [component.reconciliation, props?.reconciliation]) {
    const record = toRecord(reconciliation);
    if (
      record &&
      stringValue(record.method) === 'sum_of_parts' &&
      (record.total !== undefined || record.components !== undefined)
    ) {
      claims.push({ ...record, kind: 'sum' });
    }
  }

  return claims;
}

function appendRecordArray(target: UnknownRecord[], value: unknown): void {
  for (const item of toArray(value)) {
    const record = toRecord(item);
    if (record) target.push(record);
  }
}

function appendKindedClaims(
  target: UnknownRecord[],
  kind: AnswerArithmeticClaimKind,
  ...values: unknown[]
): void {
  for (const value of values) {
    for (const item of toArray(value)) {
      const record = toRecord(item);
      if (record) target.push({ ...record, kind });
    }
  }
}

function collectNumericBindings(envelope: unknown): UnknownRecord[] {
  const bindings: UnknownRecord[] = [];
  const seen = new WeakSet<object>();

  walk(envelope, (node) => {
    const record = toRecord(node);
    if (!record || !Array.isArray(record.numericBindings)) return;
    for (const binding of record.numericBindings) {
      const bindingRecord = toRecord(binding);
      if (!bindingRecord || seen.has(bindingRecord)) continue;
      seen.add(bindingRecord);
      bindings.push(bindingRecord);
    }
  });

  return bindings;
}

function collectNumericBindingOccurrences(envelope: unknown): BindingOccurrence[] {
  return collectNumericBindings(envelope).flatMap((binding): BindingOccurrence[] => {
    const bindingId = normalizeBindingName(bindingIdentifier(binding));
    const value = firstNumber(
      binding.value,
      binding.resolvedValue,
      toRecord(binding.reconciliation)?.total,
    );
    if (value === null) return [];
    return [
      {
        bindingId,
        component: `binding:${bindingId}`,
        value,
        scale: stringValue(binding.format ?? binding.displayScale ?? binding.scale),
        source: 'binding',
        roundingTolerance: 0,
      },
    ];
  });
}

function collectRenderedBindingOccurrences(
  components: ComponentNode[],
): BindingOccurrence[] {
  const occurrences: BindingOccurrence[] = [];

  for (const component of components) {
    walk(component.record, (node) => {
      const record = toRecord(node);
      if (!record) return;
      const bindingId = stringValue(
        record.bindingId ??
          record.bindingRef ??
          record.placeholder ??
          record.metricKey,
      );
      if (!bindingId) return;

      const parsed = parseDisplayedValue(
        record.displayValue ??
          record.formattedValue ??
          record.renderedValue ??
          record.displayedValue ??
          record.amount,
      );
      const numericValue = firstNumber(record.value, record.numericValue, record.actualValue);
      const value = parsed?.value ?? numericValue;
      if (value === null) return;

      occurrences.push({
        bindingId: normalizeBindingName(bindingId),
        component: component.id,
        value,
        scale:
          parsed?.scale ??
          stringValue(record.displayScale ?? record.scale ?? record.format),
        source: 'rendered',
        roundingTolerance: parsed?.roundingTolerance ?? 0,
      });
    });
  }

  return occurrences;
}

function parseDisplayedValue(value: unknown): {
  value: number;
  scale: string;
  roundingTolerance: number;
} | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;

  const suffixMatch = trimmed.match(/([kmb])\b/i);
  const percent = trimmed.includes('%');
  const scale = percent ? 'percent' : scaleFromSuffix(suffixMatch?.[1]);
  const multiplier = percent ? 1 : multiplierFromScale(scale);
  const numericToken = trimmed.replace(/,/g, '').match(/-?\d+(?:\.\d+)?/);
  if (!numericToken) return null;

  const parsed = Number(numericToken[0]);
  if (!Number.isFinite(parsed)) return null;

  const decimals = numericToken[0].includes('.')
    ? numericToken[0].split('.')[1]?.length ?? 0
    : 0;
  return {
    value: parsed * multiplier,
    scale,
    roundingTolerance: (multiplier * 0.5) / 10 ** decimals,
  };
}

function scaleFromSuffix(suffix: string | undefined): string {
  if (!suffix) return 'unit';
  const normalized = suffix.toLowerCase();
  if (normalized === 'k') return 'thousand';
  if (normalized === 'm') return 'million';
  if (normalized === 'b') return 'billion';
  return 'unit';
}

function multiplierFromScale(scale: string): number {
  if (scale === 'thousand') return 1_000;
  if (scale === 'million') return 1_000_000;
  if (scale === 'billion') return 1_000_000_000;
  return 1;
}

function collectComponents(envelope: unknown): ComponentNode[] {
  const components: ComponentNode[] = [];
  const seen = new WeakSet<object>();

  walk(envelope, (node) => {
    const record = toRecord(node);
    if (!record || seen.has(record)) return;
    const componentType = stringValue(record.componentType ?? record.type);
    const componentId = stringValue(record.componentId ?? record.id);
    if (!componentType && !componentId) return;
    if (!record.props && !componentType) return;
    seen.add(record);
    components.push({
      id: componentId || componentType || 'component:unknown',
      record,
    });
  });

  return components;
}

function isSumExempt(record: UnknownRecord): boolean {
  const props = toRecord(record.props);
  return [record, props].some((candidate) => {
    if (!candidate) return false;
    const audit = toRecord(candidate.arithmeticAudit);
    const exemption = stringValue(audit?.exemption ?? audit?.sum ?? candidate.sumAudit);
    return (
      candidate.neverSum === true ||
      candidate.lawWithhold === true ||
      exemption === 'never-sum' ||
      exemption === 'never_sum' ||
      exemption === 'law-withhold' ||
      exemption === 'law_withhold'
    );
  });
}

function bindingIdentifier(binding: UnknownRecord): string {
  return (
    stringValue(binding.placeholder) ||
    stringValue(binding.bindingId) ||
    stringValue(binding.metricKey) ||
    stringValue(binding.id) ||
    '<unknown-binding>'
  );
}

function normalizeBindingName(value: string): string {
  const match = value.match(/\{\{n:([^}]+)\}\}/);
  return match ? match[1] ?? value : value;
}

function sumValues(values: unknown[]): SumValuesResult {
  let total = 0;
  let found = false;
  for (const value of values) {
    const record = toRecord(value);
    const numericValue = numberValue(record?.value ?? value);
    if (numericValue === null) {
      return {
        total: null,
        nonNumericPart:
          stringValue(record?.label) ??
          stringValue(record?.key) ??
          '<unnamed>',
      };
    }
    total += numericValue;
    found = true;
  }
  return {
    total: found ? total : null,
    nonNumericPart: null,
  };
}

function withinTolerance(
  expected: number,
  actual: number,
  options: AnswerArithmeticAuditOptions,
): boolean {
  const delta = Math.abs(expected - actual);
  const absoluteTolerance = options.absoluteTolerance ?? DEFAULT_ABSOLUTE_TOLERANCE;
  const relativeTolerance = options.relativeTolerance ?? DEFAULT_RELATIVE_TOLERANCE;
  return (
    delta <= absoluteTolerance ||
    delta <= Math.abs(expected || 1) * relativeTolerance
  );
}

function severityValue(value: unknown): AnswerArithmeticFindingSeverity {
  return value === 'warning' ? 'warning' : 'error';
}

function firstNumber(...values: unknown[]): number | null {
  for (const value of values) {
    const parsed = numberValue(value);
    if (parsed !== null) return parsed;
  }
  return null;
}

function numberValue(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value !== 'string' || value.trim() === '') return null;
  const parsed = Number(value.replace(/[$,%]/g, '').replace(/,/g, ''));
  return Number.isFinite(parsed) ? parsed : null;
}

function stringValue(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

function toArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function toRecord(value: unknown): UnknownRecord | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as UnknownRecord)
    : null;
}

function walk(
  value: unknown,
  visitor: (node: unknown) => void,
  seen: WeakSet<object> = new WeakSet<object>(),
): void {
  visitor(value);
  if (value === null || typeof value !== 'object') return;
  if (seen.has(value)) return;
  seen.add(value);

  if (Array.isArray(value)) {
    for (const item of value) walk(item, visitor, seen);
    return;
  }

  for (const item of Object.values(value)) {
    walk(item, visitor, seen);
  }
}
